'use client';

import React, { useEffect } from 'react';

import { usePagination } from '@ajna/pagination';
import { Flex, SimpleGrid, VStack } from '@chakra-ui/react';

import { Loader } from '@components/custom-loader';
import Paginator from '@components/pagination/Paginator';
import { useAppSelector } from '@hooks/store';
import { useGetMentorsQuery } from '@modules/find-mentor/api';
import { MAXIMUM_MENTOR_EXPERIENCE } from '@modules/find-mentor/constants';

import { NoMentorsAvailable } from './NoMentorsAvailable';
import MentorCard from '../cards/MentorCard';

const Mentors = () => {
    const {
        expertise,
        totalExperienceMin,
        totalExperienceMax,
        orderBy,
        query,
        fromDate,
        toDate,
        domainIds,
    } = useAppSelector(state => state.reducer.mentorFilters);

    const [totalRecords, setTotalRecords] = React.useState(0);
    const { pages, pagesCount, offset, currentPage, setCurrentPage, pageSize } = usePagination({
        total: totalRecords,
        limits: {
            outer: 2,
            inner: 2,
        },
        initialState: {
            pageSize: 16,
            currentPage: 1,
        },
    });

    const { data, isFetching, isSuccess } = useGetMentorsQuery({
        offset,
        limit: pageSize,
        search: query.length > 0 ? query : undefined,
        skills: expertise?.length > 0 ? expertise.join(',') : undefined,
        totalExperienceMin: totalExperienceMin > 0 ? totalExperienceMin : undefined,
        totalExperienceMax:
            totalExperienceMax > 0 && totalExperienceMax < MAXIMUM_MENTOR_EXPERIENCE
                ? totalExperienceMax
                : undefined,
        orderBy: orderBy !== '' ? orderBy : undefined,
        fromDate: fromDate !== '' ? fromDate : undefined,
        toDate: toDate !== '' ? toDate : undefined,
        domainIds,
    });
    useEffect(() => {
        if (isSuccess) setTotalRecords(data?.count);
    }, [isSuccess, data?.count]);

    useEffect(() => {
        setCurrentPage(1);
    }, [
        expertise,
        totalExperienceMin,
        totalExperienceMax,
        orderBy,
        query,
        fromDate,
        toDate,
        domainIds,
        setCurrentPage,
    ]);

    return (
        <VStack width="100%" align="flex-start" position="relative">
            {isFetching && (
                <Loader
                    bgColor="loaderBg"
                    position="absolute"
                    height="70vh"
                    width="inherit"
                    size="lg"
                />
            )}
            <SimpleGrid
                columns={{ base: 1, sm: 2, md: 3, lg: 3, xl: 4 }}
                width="100%"
                spacing="20px"
            >
                {data?.mentors &&
                    data.mentors?.length > 0 &&
                    data.mentors?.map((mentor, index) => (
                        <MentorCard key={`key ${index}`} mentorInfo={mentor} />
                    ))}
            </SimpleGrid>
            {!isFetching && data?.mentors?.length === 0 && <NoMentorsAvailable />}
            <Flex width="100%" justify="flex-end">
                {data?.count! > 0 && (
                    <Paginator
                        pages={pages}
                        pagesCount={pagesCount}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                    />
                )}
            </Flex>
        </VStack>
    );
};

export default Mentors;
